/* eslint-disable */
import type { Token } from "@/domains/core"
import type { Address } from "viem"
import { useMemo } from "react"
import { Query, useQuery } from "@tanstack/react-query"
import chunk from "chunk"
import { formatUnits, isAddressEqual } from "viem"

import { BIGINT_ZERO, equalensMultiBalance } from "../../on-chain"

const REFRESH_INTERVAL = 30
const CHUNK_SIZE = 300

type UseTokenBalancesProps = {
  account?: any
  config: any
  equalensAddress: Address
  tokens: Array<Token>
  isLocalTokenBalances?: boolean
}

/**
 * For the record, value is a UNIT value, not a USD or a wei value.
 * 1.234 at 18 decimals is equivalent to 1234000000000000000
 * the values here are BigInt formatted through formatUnits
 * with the token decimals.
 *
 * This used to be a number, but that loses precision for large numbers. We
 * are changing this to a string to preserve precision.
 */
export type Balances = Record<Address, { value: string }>

export const useTokenBalances = ({
  account,
  config,
  equalensAddress,
  tokens,
  isLocalTokenBalances,
}: UseTokenBalancesProps): { balances: Balances; query: any } => {
  const queryKey = useMemo(() => {
    if (isLocalTokenBalances) {
      return ["tokens", "balances", "local", account]
    }
    return ["tokens", "balances", account]
  }, [account, isLocalTokenBalances])

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!account || !config || !equalensAddress) {
        return {}
      }

      const addresses = tokens.map((token) => token.address)
      const chunked = chunk(addresses, CHUNK_SIZE)
      let balancesResult: Array<any> = []
      for (let i = 0; i < chunked.length; i++) {
        const chunk = chunked[i]!

        const chunkResult = (await equalensMultiBalance({
          account,
          config,
          equalensMultiBalance: equalensAddress,
          addresses: chunk,
        })) as Array<{ bals: Array<bigint>; token: Address; tota: bigint }>

        balancesResult = balancesResult.concat(chunkResult)
      }

      const balances: Balances = {}
      if (balancesResult.length > 0) {
        balancesResult.forEach((balancesResult, index) => {
          const token = tokens[index]!
          if (isAddressEqual(token.address, balancesResult.token)) {
            const value = balancesResult.bals[0] ?? BIGINT_ZERO
            if (value > BIGINT_ZERO) {
              // update the balance as a formatted number
              balances[token.address] = {
                value: formatUnits(value, token.decimals),
              }
            }
          }
        })
      }

      // console.log("fetched new balances", balances)

      return balances
    },
    refetchInterval: 1000 * REFRESH_INTERVAL,
    staleTime: 1000 * REFRESH_INTERVAL,

    enabled: !!account && Array.isArray(tokens) && tokens.length > 0,
  })

  return { query, balances: query.data ?? {} }
}
